import { useState, useEffect } from "react";
import { Navigate, Link } from "react-router-dom";
import { SEO } from "@/components/seo/SEO";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";
import { Mail, CheckCircle, KeyRound } from "lucide-react";
import manifestLogo from "@/assets/manifest-logo.webp";

const FEATURES = [
  "AI identifies items from photos and receipts",
  "Return windows tracked automatically",
  "One-tap insurance export PDF",
  "Recall alerts, always free",
  "Private and encrypted",
];

function PasswordAuthSection() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  if (!expanded) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        className="mt-3 flex items-center justify-center gap-1.5 text-xs text-[#9CA3AF] hover:text-[#6B7280] transition-colors w-full"
      >
        <KeyRound className="h-3 w-3" />
        Sign in with password
      </button>
    );
  }

  const handleSignIn = async () => {
    if (!email.trim() || !password) return;
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
    setLoading(false);
    if (error) toast({ title: "Error", description: error.message, variant: "destructive" });
  };

  const handleSignUp = async () => {
    if (!email.trim() || !password) return;
    if (password.length < 6) {
      toast({ title: "Error", description: "Password must be at least 6 characters", variant: "destructive" });
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.signUp({ email: email.trim(), password });
    setLoading(false);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Account created", description: "You are now signed in." });
    }
  };

  return (
    <div className="mt-4 space-y-3 rounded-lg border border-[#E5E5E0] bg-white p-4">
      <p className="text-xs font-medium text-[#6B7280]">Password sign-in</p>
      <Input
        type="email"
        placeholder="Email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="min-h-[40px] rounded-lg border-[#E5E5E0]"
      />
      <Input
        type="password"
        placeholder="Password (min 6 chars)"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        className="min-h-[40px] rounded-lg border-[#E5E5E0]"
      />
      <div className="flex gap-2">
        <Button
          type="button"
          onClick={handleSignIn}
          disabled={loading}
          className="flex-1 min-h-[40px] rounded-lg bg-[#111] hover:bg-[#222] text-white text-sm"
        >
          Sign in
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={handleSignUp}
          disabled={loading}
          className="flex-1 min-h-[40px] rounded-lg border-[#E5E5E0] text-sm"
        >
          Create account
        </Button>
      </div>
    </div>
  );
}

export default function Login() {
  const { user, loading: authLoading } = useAuth();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const authRedirectUrl = "https://manifestme.me";

  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes("error_description=")) {
      const params = new URLSearchParams(hash.replace("#", ""));
      const desc = params.get("error_description")?.replace(/\+/g, " ");
      if (desc) {
        setAuthError(desc);
        window.history.replaceState(null, "", window.location.pathname);
      }
    }
  }, []);

  if (authLoading) return null;
  if (user) return <Navigate to="/dashboard" replace />;

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    const { error } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: { emailRedirectTo: authRedirectUrl },
    });
    setLoading(false);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      setSent(true);
    }
  };

  return (
    <div className="flex min-h-screen">
      <SEO
        title="Sign In – Manifest"
        description="Sign in to Manifest to manage your home inventory, track warranties, and generate insurance-ready claim packets."
        canonical="https://manifestme.me/login"
        noindex
      />
      {/* Left panel — branding */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-center bg-[#111] px-12 xl:px-20 relative overflow-hidden">
        {/* Subtle circle textures */}
        <div className="absolute top-[-200px] right-[-200px] w-[500px] h-[500px] rounded-full border border-white/[0.04]" />
        <div className="absolute bottom-[-150px] left-[-150px] w-[400px] h-[400px] rounded-full border border-white/[0.04]" />

        <div className="relative z-10 max-w-[420px]">
          <h1 className="font-editorial text-[clamp(2.2rem,3.5vw,3rem)] text-white leading-[1.1] mb-4">
            Everything logged.<br />
            <em>Nothing lost.</em>
          </h1>
          <p className="text-[15px] text-white/55 leading-relaxed mb-8">
            Your receipts, warranties, and serial numbers — all in one place. So when you actually need them, you're not hunting through old emails hoping you saved it.
          </p>
          <ul className="space-y-3">
            {FEATURES.map((f, i) => (
              <li key={i} className="flex items-center gap-3 text-[14px] text-white/70">
                <span className="h-1.5 w-1.5 rounded-full bg-[#0F766E] shrink-0" />
                {f}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Right panel — auth */}
      <div className="flex flex-1 flex-col items-center justify-center bg-[#FAFAF7] px-6 py-12">
        <div className="w-full max-w-[380px] animate-fade-in">
          {/* Logo on mobile */}
          <div className="mb-8 lg:mb-10">
            <div className="flex items-center gap-2.5 mb-6">
              <div className="h-[30px] w-[30px] rounded-lg overflow-hidden">
                <img src={manifestLogo} alt="Manifest" className="h-full w-full object-contain" />
              </div>
              <span className="font-editorial text-xl">Manifest</span>
            </div>
            <h2 className="font-editorial text-2xl text-[#111]">Welcome to Manifest</h2>
            <p className="mt-1.5 text-sm text-[#6B7280]">
              {sent ? `We sent a magic link to ${email}` : "Sign in or create an account."}
            </p>
          </div>

          {authError && (
            <div className="mb-4 rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-center text-sm text-destructive">
              <p className="font-medium">Your magic link has expired or was already used.</p>
              <p className="mt-1 text-xs text-muted-foreground">Please request a new one below.</p>
            </div>
          )}

          {sent ? (
            <div className="flex flex-col items-center gap-4 py-8">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#0F766E]/10">
                <CheckCircle className="h-7 w-7 text-[#0F766E]" />
              </div>
              <p className="text-center text-sm text-[#6B7280]">
                Click the link in your email to sign in.
              </p>
              <Button variant="ghost" onClick={() => setSent(false)} className="mt-2 min-h-[44px]">
                Use a different email
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <Button
                type="button"
                variant="outline"
                className="w-full min-h-[44px] rounded-lg font-semibold gap-2 bg-white border-[#E5E5E0] hover:bg-[#F0EFEB]"
                onClick={async () => {
                  const { error } = await supabase.auth.signInWithOAuth({
                    provider: "google",
                    options: {
                      redirectTo: authRedirectUrl,
                    },
                  });
                  if (error) toast({ title: "Error", description: error.message, variant: "destructive" });
                }}
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                Continue with Google
              </Button>

              <Button
                type="button"
                variant="outline"
                className="w-full min-h-[44px] rounded-lg font-semibold gap-2 bg-white border-[#E5E5E0] hover:bg-[#F0EFEB]"
                onClick={async () => {
                  const { error } = await supabase.auth.signInWithOAuth({
                    provider: "apple",
                    options: {
                      redirectTo: authRedirectUrl,
                    },
                  });
                  if (error) toast({ title: "Error", description: error.message, variant: "destructive" });
                }}
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
                Continue with Apple
              </Button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-[#E5E5E0]" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-[#FAFAF7] px-2 text-[#9CA3AF]">or</span>
                </div>
              </div>

              <form onSubmit={handleLogin} className="space-y-4">
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-[#9CA3AF]" />
                  <Input
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 min-h-[44px] rounded-lg bg-white border-[#E5E5E0]"
                    required
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full min-h-[44px] rounded-lg font-semibold bg-[#111] hover:bg-[#222] text-white"
                  disabled={loading}
                >
                  {loading ? "Sending..." : "Send magic link"}
                </Button>
              </form>
            </div>
          )}

          {/* Password auth for preview testing */}
          {!sent && <PasswordAuthSection />}

          <p className="mt-8 text-center text-xs text-[#9CA3AF]">
            By signing in you agree to our{" "}
            <Link to="/terms" className="underline hover:text-[#6B7280]">terms</Link> and{" "}
            <Link to="/privacy" className="underline hover:text-[#6B7280]">privacy policy</Link>.
          </p>
        </div>
      </div>
    </div>
  );
}
